import {
  getMessaging,
  getToken,
  isSupported,
  deleteToken,
  onMessage,
} from 'firebase/messaging';
import app from './firebase';
import { withTimeout } from '../utils/asyncTimeout';
import { newRequestId, operation } from './operationsService';
import { getAppWorkerRegistration } from './webUpdateService';

function deviceId() {
  let id = localStorage.getItem('inventory-push-device');
  if (!id) {
    id = newRequestId();
    localStorage.setItem('inventory-push-device', id);
  }
  return id;
}
export type PushProgress = 'permission' | 'worker' | 'token' | 'saving';
async function activeWorker(
  registration: ServiceWorkerRegistration,
): Promise<void> {
  if (registration.active?.state === 'activated') return;
  return new Promise((resolve, reject) => {
    const finish = (error?: string) => {
      clearTimeout(timeout);
      clearInterval(poll);
      if (error) reject(new Error(error));
      else resolve();
    };
    const poll = setInterval(() => {
      if (registration.active?.state === 'activated') finish();
      else if (
        registration.installing?.state === 'redundant' &&
        !registration.active
      )
        finish('PUSH_WORKER_FAILED');
    }, 100);
    const timeout = setTimeout(() => finish('PUSH_WORKER_TIMEOUT'), 25000);
  });
}
export async function enablePush(
  onProgress?: (progress: PushProgress) => void,
): Promise<void> {
  if (
    typeof window === 'undefined' ||
    !window.isSecureContext ||
    !('Notification' in window) ||
    !('serviceWorker' in navigator)
  )
    throw new Error('PUSH_UNAVAILABLE');
  if (!process.env.EXPO_PUBLIC_FCM_VAPID_KEY)
    throw new Error('PUSH_CONFIG_MISSING');
  onProgress?.('permission');
  // Keep the permission prompt inside the click's user gesture (not after an SDK await).
  const permission =
    Notification.permission === 'default'
      ? Notification.requestPermission()
      : Promise.resolve(Notification.permission);
  if (
    (await withTimeout(permission, 20000, 'PUSH_PERMISSION_TIMEOUT')) !==
    'granted'
  )
    throw new Error('PUSH_PERMISSION_DENIED');
  if (!(await withTimeout(isSupported(), 10000, 'PUSH_UNAVAILABLE')))
    throw new Error('PUSH_UNAVAILABLE');
  onProgress?.('worker');
  const serviceWorkerRegistration = await withTimeout(
    getAppWorkerRegistration(),
    20000,
    'PUSH_WORKER_TIMEOUT',
  );
  await activeWorker(serviceWorkerRegistration);
  onProgress?.('token');
  const token = await withTimeout(
    getToken(getMessaging(app), {
      vapidKey: process.env.EXPO_PUBLIC_FCM_VAPID_KEY,
      serviceWorkerRegistration,
    }),
    30000,
    'PUSH_TOKEN_TIMEOUT',
  );
  onProgress?.('saving');
  await withTimeout(
    operation('notificationOperation', {
      action: 'registerDevice',
      deviceId: deviceId(),
      token,
      transport: 'fcm',
    }),
    20000,
    'PUSH_SAVE_TIMEOUT',
  );
  localStorage.setItem('inventory-push-enabled', 'true');
}

export async function disablePush(): Promise<void> {
  if (typeof localStorage === 'undefined') return;
  const saved = localStorage.getItem('inventory-push-device');
  if (!saved || localStorage.getItem('inventory-push-enabled') !== 'true')
    return;
  if (saved)
    await operation('notificationOperation', {
      action: 'unregisterDevice',
      deviceId: saved,
    });
  if (await isSupported()) await deleteToken(getMessaging(app));
  localStorage.removeItem('inventory-push-enabled');
}
export function watchPush(onOpen: () => void): () => void {
  let unsubscribe: (() => void) | undefined;
  let active = true;
  isSupported()
    .then((supported) => {
      if (!supported || !active) return;
      unsubscribe = onMessage(getMessaging(app), (message) => {
        if (Notification.permission !== 'granted') return;
        const notification = new Notification(
          message.notification?.title ?? 'GOPR Inventory',
          { body: message.notification?.body },
        );
        notification.onclick = () => {
          notification.close();
          onOpen();
        };
      });
      if (
        localStorage.getItem('inventory-push-enabled') === 'true' &&
        Notification.permission === 'granted'
      )
        enablePush().catch(() => {});
    })
    .catch(() => {});
  return () => {
    active = false;
    unsubscribe?.();
  };
}
